import { lazy, ReactElement, ReactNode, Suspense, useState } from 'react'

import CubeIcon from '@heroicons/react/outline/esm/CubeIcon.js'
import PencilAltIcon from '@heroicons/react/outline/esm/PencilAltIcon.js'
import UserAddIcon from '@heroicons/react/outline/esm/UserAddIcon.js'
import ViewGridIcon from '@heroicons/react/outline/esm/ViewGridIcon.js'
import { Link, useNavigate } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthMember, useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

import { NavbarControlItem } from '../constants.js'

const UserImportModal = lazy(() =>
  import('../../UserImport/index.js').then(m => ({
    default: m.UserImportModal,
  })),
)
const CreateSpaceModal = lazy(() =>
  import('@tribeplatform/react-components/Space').then(m => ({
    default: m.CreateSpaceFromTemplateModal,
  })),
)
const CollectionFormModal = lazy(() =>
  import('../../Collection/CollectionFormModal.js').then(m => ({
    default: m.CollectionFormModal,
  })),
)

type CreateMenuItem = {
  name?: string
  href?: string
  icon?: ReactNode
  as?: 'a' | typeof Link
  key?: string
  action?: () => void
}

export type NavbarControlProfileProps = {
  item: NavbarControlItem
  presentation: ReactElement
  className?: string
}

export const NavbarControlCreateItem = ({
  item,
  presentation,
  className,
}: NavbarControlProfileProps): ReactElement => {
  const { $t } = useI18n()
  const { data: authToken } = useAuthToken()
  const { isGuest } = useAuthMember()
  const { network } = authToken || {}
  const navigate = useNavigate()

  const [openSpaceModal, setOpenSpaceModal] = useState(false)
  const [openCollectionModal, setOpenCollectionModal] = useState(false)
  const [openMemberInviteModal, setOpenMemberInviteModal] = useState(false)

  const [canCreateSpace, canCreateCollection, canInviteMembers] =
    hasScopesPermission(network, [
      'createSpace',
      'createCollection',
      'inviteMembers',
    ])
  const canCreatePost = !isGuest

  /**
   * @tag Vite migration
   * Dropdown produces hydration error because of prop `id` did not match.
   * As a workaround, Dropdown is initialized client-side only
   */
  const { isFirstRender } = useFirstRenderContext()

  if (
    !canCreatePost &&
    !canCreateSpace &&
    !canCreateCollection &&
    !canInviteMembers
  ) {
    return null
  }

  if (isFirstRender) {
    return presentation
  }

  const menuOptions: Array<CreateMenuItem> = []

  if (canCreatePost) {
    menuOptions.push({
      name: $t({
        defaultMessage: 'Add post',
        id: 'Posts.Add',
      }),
      icon: <PencilAltIcon />,
      action: () => navigate('/new'),
      key: 'add-post',
    })
  }
  if (canCreateSpace) {
    menuOptions.push({
      name: $t({
        defaultMessage: 'Add {SPACE}',
        id: 'Navbar.Controls.AddSpace',
      }),
      icon: <CubeIcon />,
      action: () => setOpenSpaceModal(true),
      key: 'add-space',
    })
  }
  if (canCreateCollection) {
    menuOptions.push({
      name: $t({
        defaultMessage: 'Add {COLLECTION}',
        id: 'Navbar.Controls.AddCollection',
      }),
      icon: <ViewGridIcon />,
      action: () => setOpenCollectionModal(true),
      key: 'add-collection',
    })
  }
  if (canInviteMembers) {
    menuOptions.push({
      name: $t({
        defaultMessage: 'Invite {MEMBERS}',
        id: 'Navbar.Controls.AddMembers',
      }),
      icon: <UserAddIcon />,
      action: () => setOpenMemberInviteModal(true),
      key: 'invite-members',
    })
  }

  return (
    <>
      <Dropdown placement="bottom-end" className={className}>
        <Dropdown.ButtonMinimal
          className="rounded-full navbar-create-button"
          aria-label={item.label}
        >
          {presentation}
        </Dropdown.ButtonMinimal>
        <Dropdown.Items className="navbar-create-button__dropdown">
          {menuOptions.map(item => {
            if (item?.name === '-') {
              return <Dropdown.Divider key="static-key-navbar-divider" />
            }

            if (item.as === 'a') {
              return (
                <Dropdown.Item
                  leadingIcon={item.icon as any}
                  key={item.name}
                  as="a"
                  href={item.href || '#'}
                  onClick={item.action}
                >
                  {item.name}
                </Dropdown.Item>
              )
            }

            if (item.href) {
              return (
                <Dropdown.Item
                  leadingIcon={item.icon as any}
                  key={item.name}
                  as={Link}
                  to={item.href}
                  onClick={item.action}
                >
                  {item.name}
                </Dropdown.Item>
              )
            }

            return (
              <Dropdown.Item
                leadingIcon={item.icon as any}
                key={item.name}
                onClick={item.action}
              >
                {item.name}
              </Dropdown.Item>
            )
          })}
        </Dropdown.Items>
      </Dropdown>

      {openSpaceModal ? (
        <Suspense fallback={<></>}>
          <CreateSpaceModal
            open={openSpaceModal}
            onClose={() => {
              setOpenSpaceModal(false)
            }}
          />
        </Suspense>
      ) : null}
      {openCollectionModal ? (
        <Suspense fallback={<></>}>
          <CollectionFormModal
            open={openCollectionModal}
            onClose={() => {
              setOpenCollectionModal(false)
            }}
          />
        </Suspense>
      ) : null}

      {openMemberInviteModal && (
        <Suspense fallback={<></>}>
          <UserImportModal
            open={openMemberInviteModal}
            onClose={() => {
              setOpenMemberInviteModal(false)
            }}
          />
        </Suspense>
      )}
    </>
  )
}
